import {
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DownloadOutlined,
  FacebookFilled,
  LinkedinFilled,
  ShareAltOutlined,
  TwitterSquareFilled,
  UserOutlined,
} from "@ant-design/icons"
import React, { useCallback } from "react"
import {
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share"
import styled, { css } from "styled-components"
import Breakpoints from "../../../components/shared/breakpoints"
import SmartLink from "../../../components/shared/smart-link"
import { Spacer } from "../../atoms/layout-primitives"
import Block from "../../atoms/layout-primitives/block"
import { BlogGrid } from "../../atoms/layout-primitives/grid"
import VariantContainer from "../../atoms/variant-container"
import Breadcrumbs from "../../molecules/breadcrumbs"
import IconTitle from "../../molecules/icon-title"
import { BT } from "../../styles/blog-typography"
import InternalRoutes from "../../util/internal-routes"

const InfoItem = styled(VariantContainer)`
  display: inline-block;
  ${BT.sm.info`400`}
  opacity: 0.45;
  padding-right: 12px;
  span {
    margin-right: 8px;
  }
`

/** Displays "Copied" bubble */
const Toast = styled.span<{ isVisible: boolean }>`
  visibility: ${(props) => (props.isVisible ? "visible" : "hidden")};
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  ${BT.sm.info`400`}
  font-size: 16px;
  background-color: var(--emerald);
  color: #fff;
  text-align: center;
  border-radius: 4px;
  padding: 8px;
  position: absolute;
  z-index: 1;
  bottom: 100%;
  margin-left: -75%;
  transition: opacity 0.2s ease-in-out, visibility 0.2s ease-in-out;
  white-space: nowrap;

  &::after {
    content: "";
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: var(--emerald) transparent transparent transparent;
  }

  /* Tap target changes, so move the toast too */
  @media (min-width: ${Breakpoints.md}) {
    margin-left: -150%;
    bottom: 150%;
  }
`

const CopyShareButton = styled.button`
  display: inline-grid;
  position: relative;
  align-content: center;
  height: 40px;
  width: 40px;

  background-color: transparent;
  border: none;
  padding: 0px;
  font: inherit;
  color: inherit;
  cursor: pointer;
`

const DownloadTapTarget = styled(SmartLink)`
  text-decoration: inherit;
`

export interface IArticleFrontmatter {
  title: string
  content: {
    author?: string
    publishDate?: string
    updatedAt?: string
    pdf?: {
      file: {
        url: string
      }
    }
    tags?: {
      topic: { name: string; slug: string }
      category: { name: string; slug: string }
    }
  }
  readTime?: string
  href?: string
}

interface IArticleHeaderProps {
  frontmatter: IArticleFrontmatter
}

/**
 * Template for generated blog article header
 * @param frontmatter - Frontmatter of the article from Contentful
 */
const ArticleHeader = ({ frontmatter }: IArticleHeaderProps) => {
  const [isToastVisible, setIsToastVisible] = React.useState(false)

  /** Copies the url to the users clipboard and triggers a toast affect */
  const handleCopyToClipboard = useCallback(
    (url: string) => {
      setIsToastVisible(true)
      // navigator clipboard requires https, will be undefined on staging
      if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
      } else {
        // fallback to using hidden textarea
        const el = document.createElement("textarea")
        el.value = url
        document.body.appendChild(el)
        el.select()
        document.execCommand("copy")
        document.body.removeChild(el)
      }
      setTimeout(() => {
        setIsToastVisible(false)
      }, 1000)
    },
    [setIsToastVisible]
  )

  // Destructure the frontmatter
  const {
    title,
    content: { author, publishDate, updatedAt, pdf, tags },
    readTime,
    href,
  } = frontmatter

  // Determine the date to display
  // If PublishDate was supplied use it, otherwise fallback on updatedAt
  const date = publishDate || updatedAt

  // Social media sharing props
  const shareButtonProps = {
    title: title,
    url: href,
  }

  return (
    <BlogGrid>
      <Block col={{ lg: "3/-3" }}>
        <Spacer height={{ sm: "16px", md: "32px" }} />

        <Block>
          {tags &&
            // developers page has different breadcrumbs
            (tags.category.slug === "developers" ? (
              <Breadcrumbs>
                <SmartLink to={InternalRoutes.Blog()}>Home</SmartLink>
                <SmartLink to={InternalRoutes.Blog(tags.category)}>
                  {tags.category.name}
                </SmartLink>
              </Breadcrumbs>
            ) : (
              <Breadcrumbs>
                <SmartLink to={InternalRoutes.Blog()}>Home</SmartLink>
                <SmartLink to={InternalRoutes.Blog(tags.category)}>
                  {tags.category.name}
                </SmartLink>
                <SmartLink to={InternalRoutes.Blog(tags.category, tags.topic)}>
                  {tags.topic.name}
                </SmartLink>
              </Breadcrumbs>
            ))}
        </Block>
        <Spacer height={{ sm: "16px", md: "24px" }} />

        <Block
          variants={[
            css`
              ${BT.sm.h1`500`};
              margin-bottom: 24px;
            `,
            css`
              ${BT.md.h1`500`};
              margin-bottom: 16px;
            `,
            css`
              ${BT.lg.h1`500`};
            `,
          ]}
          as="h1"
        >
          {title}
        </Block>
        <Block>
          {date && (
            <InfoItem>
              <CalendarOutlined />
              {date}
            </InfoItem>
          )}
          {/* If all 3 are present, force 8px line break on mobile */}
          {date && readTime && author && (
            <VariantContainer
              variants={[
                css`
                  display: block;
                  height: 8px;
                `,
                css`
                  display: none;
                `,
              ]}
            />
          )}
          {readTime && (
            <InfoItem>
              <ClockCircleOutlined />
              {readTime}
            </InfoItem>
          )}
          {author && (
            <InfoItem>
              <UserOutlined />
              By: {author}
            </InfoItem>
          )}
        </Block>
        <Spacer height={{ sm: "20px", md: "32px" }} />
        <VariantContainer
          variants={[
            css`
              display: block;
            `,
            css`
              display: flex;
              align-content: center;
              justify-content: space-between;
            `,
          ]}
        >
          <VariantContainer
            variants={[
              css`
                button {
                  display: inline-grid;
                  align-content: center;
                  height: 40px;
                  width: 40px;
                }
                font-size: 20px;
                text-align: left;
                text-decoration: inherit;
              `,
              css`
                button {
                  height: 24px;
                  width: 24px;
                  margin-right: 16px;
                }
                font-size: 24px;
              `,
            ]}
          >
            <FacebookShareButton
              quote={shareButtonProps.title}
              url={shareButtonProps.url}
            >
              <FacebookFilled />
            </FacebookShareButton>
            <TwitterShareButton {...shareButtonProps}>
              <TwitterSquareFilled />
            </TwitterShareButton>
            <LinkedinShareButton url={shareButtonProps.url}>
              <LinkedinFilled />
            </LinkedinShareButton>
            <CopyShareButton
              onClick={() => handleCopyToClipboard(shareButtonProps.url)}
            >
              <Toast isVisible={isToastVisible}>
                <CheckCircleOutlined /> Copied
              </Toast>
              <ShareAltOutlined />
            </CopyShareButton>
          </VariantContainer>
          <VariantContainer
            variants={[
              null,
              css`
                align-self: center;
              `,
            ]}
          >
            {pdf && (
              <>
                <Spacer height={{ sm: "32px", md: "0" }} />
                <DownloadTapTarget
                  to={pdf.file.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconTitle
                    icon={<DownloadOutlined />}
                    title="Download Article PDF"
                    textVariants={[
                      css`
                        ${BT.sm.info`500`}
                        text-transform: uppercase;
                        color: var(--burnt-orange);
                        letter-spacing: 0.12em;
                      `,
                    ]}
                    iconVariants={[
                      css`
                        font-size: 20px;
                      `,
                    ]}
                  />
                </DownloadTapTarget>
              </>
            )}
          </VariantContainer>
        </VariantContainer>
      </Block>
    </BlogGrid>
  )
}

export default ArticleHeader
